export const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE ?? "http://localhost:3001";

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions {
  token?: string;
  signal?: AbortSignal;
  _retry?: boolean;
}

const AUTH_PATHS = [
  "/api/auth/teacher/login",
  "/api/auth/student/login",
  "/api/auth/student/google",
  "/api/auth/refresh",
];

const TOKEN_KEY = "yuzhou.accessToken";
const REFRESH_KEY = "yuzhou.refreshToken";
const USER_KEY = "yuzhou.user";
export const TOKEN_REFRESHED_EVENT = "yuzhou:token-refreshed";

let refreshing: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  if (refreshing) return refreshing;

  refreshing = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken }),
        credentials: "omit",
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        accessToken: string;
        refreshToken: string;
        user: unknown;
      };
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      window.dispatchEvent(
        new CustomEvent(TOKEN_REFRESHED_EVENT, {
          detail: { accessToken: data.accessToken, user: data.user },
        }),
      );
      return data.accessToken;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function handleUnauthorized() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // ignore storage errors
  }
  const path = window.location.pathname;
  const target = path.startsWith("/teacher")
    ? "/teacher/login"
    : path.startsWith("/student")
      ? "/student/login"
      : "/";
  if (path === target) return;
  window.location.assign(target);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: ApiOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: opts.signal,
    credentials: "omit",
  });

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const isAuthPath = AUTH_PATHS.some((p) => path.endsWith(p));
    if (res.status === 401 && !isAuthPath) {
      if (!opts._retry) {
        const newToken = await tryRefresh();
        if (newToken) {
          return request<T>(method, path, body, {
            ...opts,
            token: newToken,
            _retry: true,
          });
        }
      }
      handleUnauthorized();
    }
    const msg =
      (parsed as { message?: string | string[] })?.message ??
      `HTTP ${res.status}`;
    throw new ApiError(
      res.status,
      parsed,
      Array.isArray(msg) ? msg.join("; ") : String(msg),
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, opts?: ApiOptions) =>
    request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: ApiOptions) =>
    request<T>("POST", path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: ApiOptions) =>
    request<T>("PATCH", path, body, opts),
  del: <T>(path: string, opts?: ApiOptions) =>
    request<T>("DELETE", path, undefined, opts),
};
